import React from "react";
import { Box } from "@mui/material";
import { tallyTokens } from "../../theme.tsx";
import "../../styles/conversation-activity.css";

export type ConversationActivityStatus = "working" | "ready";

interface ConversationActivityDotProps {
  status: ConversationActivityStatus;
  /** Diameter in px. Defaults to the drawer-row size; the TopBar badge passes
   *  its own. */
  size?: number;
}

const DEFAULT_SIZE = 8;

/**
 * A small round indicator: pulsing while a conversation is answering, solid
 * once the answer is ready and unopened. Deliberately not a spinner - see
 * `ideas/IDEA-006-concurrent-conversations.md` in ai-team for the reasoning.
 *
 * The two states render the same DOM element with only its class/colour
 * changing, rather than swapping between two elements - one element changing
 * state reads as continuity, a swap reads as a glitch.
 */
const ConversationActivityDot: React.FC<ConversationActivityDotProps> = ({
  status,
  size = DEFAULT_SIZE,
}) => (
  <Box
    className={
      status === "working" ? "conversation-activity-dot--working" : undefined
    }
    data-testid="conversation-activity-dot"
    data-status={status}
    sx={{
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: "50%",
      flexShrink: 0,
      // working: an ink tone, which reads clearly against the drawer's light
      // surface2 fill. ready: the same brand accent the notifications badge
      // already uses for "needs attention" - no new colour vocabulary.
      backgroundColor:
        status === "working" ? tallyTokens.ink2 : tallyTokens.brand,
    }}
  />
);

export default ConversationActivityDot;
